import React, {ChangeEvent, useCallback, useEffect, useRef, useState} from "react";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import {Field} from "formik";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {concepts, User} from "fearless-common";
import { Alert, Button } from "fearless-common-ui";
import {validateEmail} from "../../utils/validations";
import Description from "../Description";
import UserCard from "../layout/UserCard";
import InviteButton from "../InviteButton";
import Loading from "../Loading";
import Header from "../Header";
import InviteEmailButton from "./InviteEmailButton";
import GenerateGroupJoinLink from "./GenerateGroupJoinLink";
import {backendEndpoints} from "../../libs/backendEndpoints";


/**
 * Modal box in which the logged-in user can invite other users to the group;
 */
export default function InviteUserModal({
    groupId, reloadGroup, groupIsSubscribed
}: {
    /** Group into which the logged-in user can invite others users */
    groupId: number;
    /** Callback to refresh the group data */
    reloadGroup: () => void;
    /** True if the group has a subscription */
    groupIsSubscribed: boolean;
}) {
    const timeout = useRef<NodeJS.Timeout | null>(null);
    const [show, setShow] = useState(false);

    // The query for the search input
    const [query, setQuery] = useState('');
    // The query used for the last API call
    const [queryForCall, setQueryForcall] = useState(query);
    const [users, setUsers] = useState<User[]>([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const searchInputRef = useRef<HTMLInputElement | null>(null);

    const handleClose = () => {
        setShow(false);
        setQuery('')
        setQueryForcall('');
        setUsers([])
        setError('')
        setLoading(false)
        reloadGroup()
    }
    const handleShow = useCallback(() => setShow(true), [setShow]);

    useEffect(() => {
        setLoading(false);

        if (timeout.current) {
            clearTimeout(timeout.current);
        }

        timeout.current = setTimeout(() => {
            setLoading(false)
            setError('')

            if (query) {
                setLoading(true)
                axios.get(backendEndpoints.getUsersSearch, { params: { query }})
                    .then(({ data }) => {
                        setUsers(data.data)
                    })
                    .catch(e => {
                        const { message } = e.response.data;
                        setError(message)
                    })
                    .finally(() => {
                        setLoading(false);
                        setQueryForcall(query);
                    })
            }
        }, 500)
    }, [query])

    const hasIncompleteEmail = query.indexOf('@') > -1 && !validateEmail(query);
    const width = 36;

    return (
        <>
            <OverlayTrigger overlay={<Tooltip id="invite-members">Add {concepts.GroupMembers.toLowerCase()}</Tooltip>}>
                <Button
                  onClick={handleShow}
                  size="sm"
                  className="rounded-pill border-0 mb-0 text-white text-center" style={{ backgroundColor: '#5D6D84', width, height: width }}
                  dataTestId={`invite-users-button`}
                >
                    <i className="fa fa-plus m-1" />
                </Button>
            </OverlayTrigger>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Add a new {concepts.GroupMember.toLowerCase()}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Description className="line-height-1-5">
                        Search for an existing {concepts.Photo51} user or invite new users by typing in their email.
                    </Description>
                    <Field
                        value={query}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setQuery(e.target.value);
                            if (success) {
                                // Clear the success message, because it has become irrelevant
                                // if the user is searching again.
                                setSuccess('');
                            }
                        }}
                        className="form-control mb-2"
                        placeholder="Search..."
                        name="query"
                        autoFocus
                        autoComplete="off"
                        innerRef={searchInputRef}
                    />
                    {hasIncompleteEmail && <div className="text-xs text-danger">Please complete the email address.</div>}
                    <ButtonGroup className="d-flex flex-column mb-2">
                        {query && !loading && Array.isArray(users) && (users.length > 0
                                ? users.map((user, idx) => (
                                    <UserCard key={user.id} user={user} className={idx > 0 ? 'border-top border-neutral-300': ''}>
                                        <InviteButton
                                            user={{
                                                id: user.id,
                                                first_name: user.first_name,
                                                is_subscribed: Boolean(user.is_subscribed),
                                            }}
                                            groupId={groupId}
                                            onSuccess={reloadGroup}
                                            groupIsSubscribed={groupIsSubscribed}
                                        />
                                    </UserCard>
                                ))
                                : validateEmail(query)
                                    ? (
                                        <InviteEmailButton
                                            reload={reloadGroup}
                                            groupId={groupId}
                                            onSuccess={(message) => {
                                                // Provide the user with some feedback;
                                                setSuccess(message || '')
                                                // Automatically let de message disappear
                                                setTimeout(() => {
                                                    setSuccess('')
                                                }, 3000);

                                                // Clear the query and focus on the input, so the user
                                                // can quickly invite more people;
                                                setQuery('');
                                                searchInputRef.current?.focus();
                                            }}
                                            email={query}
                                        />
                                    )
                                    : (!hasIncompleteEmail ? <Alert variant="info">No users were found for '{queryForCall}'</Alert> : null)
                        )}
                    </ButtonGroup>
                    {error && <Alert className="alert-danger">{error}</Alert>}
                    {success && <Alert className="alert-success">{success}</Alert>}
                    {loading && <Loading />}

                    <section className="mb-5">
                        <Header component="h5" className="mb-0 mt-5">Share {concepts.Group.toLowerCase()} link</Header>
                        <GenerateGroupJoinLink groupId={groupId} />
                    </section>
                </Modal.Body>
            </Modal>
        </>
    )
}
